/** El documento que arma el servidor para el manual de organización. */

export type NodoManual = {
  id: number
  padre_id: number | null
  cargo: string | null
  nivel: string | null
  departamento: string | null
  ocupante: string | null
  vacante: boolean
  nota: string | null
  reporta_tambien_a: number[]
}

export type OrganigramaManual = {
  nombre: string
  tipo: string
  descripcion: string | null
  departamento: string | null
  nodos: NodoManual[]
}

export type PasoManual = {
  orden: number
  tipo: string
  titulo: string
  descripcion: string | null
  responsable: string | null
  rama_si: string | null
  rama_no: string | null
  es_funcion: boolean
}

export type ProcesoManual = {
  nombre: string
  objetivo: string | null
  responsable: string | null
  es_transversal: boolean
  pasos: PasoManual[]
}

export type EtapaManual = {
  titulo: string
  meta: string | null
  fecha: string | null
  estado: string
}

export type ObjetivoManual = {
  enunciado: string
  indicador: string | null
  linea_base: string | null
  meta: string | null
  unidad: string | null
  responsable: string | null
  fecha_fin: string | null
  avance: number
  etapas: EtapaManual[]
}

export type PilarManual = {
  origen: string
  titulo: string
  descripcion: string | null
  avance: number
  objetivos: ObjetivoManual[]
}

export type DocumentoManual = {
  generado_en: string
  identidad: {
    nombre: string
    razon_social: string | null
    rif: string | null
    direccion: string | null
    telefono: string | null
    email: string | null
    sitio_web: string | null
    vision: string | null
    mision: string | null
    valores: string[]
  }
  estrategia: { avance: number; pilares: PilarManual[] }
  /**
   * Las áreas por código. ⚠ **Opcional**: las ediciones definitivas congeladas antes
   * del 17/10/2026 no la tienen, y abrirlas no puede romperse por ello.
   */
  estructura?: AreaManual[]
  organigramas: OrganigramaManual[]
  procesos: ProcesoManual[]
}

export type AreaManual = {
  codigo: string | null
  siglas: string | null
  nombre: string
  descripcion: string | null
  responsable: string | null
  ocupante: string | null
  interino: boolean
  depende_de: string | null
  cargos: string[]
}

export type VersionManual = {
  id: number
  version: number
  notas: string | null
  publicado_por: number | null
  creado_en: string
}

/**
 * El numeral de un proceso.
 *
 * ⚠ **El transversal lleva ⊕ y no un romano.** «Cumplimiento Regulatorio» no ocurre
 * después de la entrega: ocurre durante todo. Numerarlo como la etapa VIII haría que
 * quien lea el manual entienda que el control normativo es un trámite del final.
 */
export function numeral(indice: number, transversal: boolean): string {
  if (transversal) return "⊕"
  const romanos = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"]
  return romanos[indice] ?? String(indice + 1)
}

/** De la lista plana a hijos por padre, para pintar la estructura con sangría. */
export function porPadre(nodos: NodoManual[]): Map<number | null, NodoManual[]> {
  const mapa = new Map<number | null, NodoManual[]>()
  const conocidos = new Set(nodos.map((n) => n.id))
  for (const nodo of nodos) {
    // Un nodo cuyo padre no está en la lista cuelga de la raíz, no se pierde.
    const clave = nodo.padre_id !== null && conocidos.has(nodo.padre_id) ? nodo.padre_id : null
    const lista = mapa.get(clave) ?? []
    lista.push(nodo)
    mapa.set(clave, lista)
  }
  return mapa
}
